import * as Sentry from "@sentry/react";
import React, { createContext, useContext, useState } from 'react';

import ErrorModal from 'components/ErrorModal';

import { parseErrorMessages } from './parseErrorMessages';

import { t } from "i18n/index";

const ErrorContext = createContext();

export const useErrorController = () => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = (record, validateFunction, skipLoading=false) => {
    if (!skipLoading) {
      setLoading(true)
    }
    validateFunction(record);
  };

  const handleError = async (error, array=false) => {
    let errorMessage;
    if (array) {
      errorMessage = error;
      
    } else if (typeof error === 'string') {
      errorMessage = [error];

    } else {
      errorMessage = await getApiErrorMessage(error);
    }
    const message = errorMessage ? errorMessage : t('errors:default');
    setErrorMessage(message);
    setOpenErrorModal(true);
  };

  const getApiErrorMessage = async (error) => {
    if (!error.response) {
      if (process.env.REACT_APP_WEB_ENV === 'production') {
        Sentry.captureException(error);
      }
      return [t('errors:default')];
    }

    if (404 === error.response.status) {
      if (error.response.url.includes("authentication")) {
        return [ t('errors:unableToValidateCredentials') ];
      } else {
        return [ t('errors:notFound') ];
      }
    } else if (401 === error.response.status) {
      localStorage.clear();
      return [ `${t('errors:unableToValidateCredentials')}. ${t('errors:pleaseRefresh')}` ];
    } else {
      const errorMessages = await parseErrorMessages(error);
      return errorMessages;
    }
  };

  const handleCloseErrorModal = () => {
    setErrorMessage(null);
    setOpenErrorModal(false);
    setLoading(false);
  };

  const ErrorModalComponent = () => {
    if (!errorMessage) return null;
    return (
       <ErrorModal message={errorMessage} open={openErrorModal} onClose={handleCloseErrorModal} />
    );
  };

  return {
    hasErrorMessage: !!errorMessage,

    loading,
    setLoading,

    validate,
    handleError,
    handleCloseErrorModal,

    ErrorModalComponent,
  };
};

// eslint-disable-next-line react/prop-types
export const ErrorProvider = ({ children }) => {
  const errorController = useErrorController();

  return (
    <ErrorContext.Provider value={errorController}>
      {children}
      {errorController.ErrorModalComponent()}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  return useContext(ErrorContext);
};
